import { clsx } from "clsx";
import {
  Children,
  type CSSProperties,
  forwardRef,
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useDataAttributes } from "../../hooks.ts";
import { getChildrenFromFragment } from "../../utils/jsx/getChildrenFromFragment.ts";
import { LoadingSpinner } from "../ProgressSpinner/LoadingSpinner.tsx";
import style from "./CardGrid.module.css";

/**
 * The values represent the CSS grid area name.
 * Each area can have multiple cards.
 *
 * |----------------------|
 * |    Header    |       |
 * |--------------|       |
 * |      |       |       |
 * | Left | Right | Panel |
 * |      |       |       |
 * |--------------|       |
 * |    Footer    |       |
 * |----------------------|
 */
enum CardGridArea {
  Header = "header",
  Left = "left",
  Right = "right",
  Footer = "footer",
  Panel = "panel",
}

export type valueof<T> = T[keyof T];
type Layout = Map<valueof<typeof CardGridArea>, ReactNode[]>;

//! Every card in a CardGrid MUST extend this interface.
export interface GridCard {
  area?: CardGridArea;
}

export interface CardGridProps {
  children?: ReactNode; // children must be defined. This is a duplicate. Mistake from React types?
  isLoading?: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * Every child MUST have an "area" property sets to CardGridArea (extends CardViewGrid)
 */
const CardGrid = forwardRef<HTMLDivElement, CardGridProps>(function CardGrid(props, ref) {
  const [layout, setLayout] = useState<Layout>();

  useEffect(() => {
    const layoutContent: Layout = new Map();

    // If the CardGrid is used in a HOC, we usually then pass the Cards (children) in a Fragment.
    // We get rid of the Fragment as we need to travel through the "real" children (the Cards) to place them appropriately.
    const children = getChildrenFromFragment(props.children);

    Children.forEach(children as ReactElement, (child: ReactElement) => {
      if (child) {
        let area: valueof<typeof CardGridArea> = child.props.area;

        // Try to fake area with the previous, and deprecated, position prop
        if (!area) {
          const x: number | undefined = child.props.position?.x;

          if (x === 0 || x == null) {
            area = CardGridArea.Left;
          } else if (x === 1) {
            area = CardGridArea.Right;
          } else if (x === 2) {
            area = CardGridArea.Panel;
          }
        }

        if (area) {
          layoutContent.set(area, [...(layoutContent.get(area) ?? []), child]);
        }
      }
    });

    setLayout(layoutContent);
  }, [props.children]);

  const header = useMemo(() => layout?.get(CardGridArea.Header)?.filter((c) => !!c), [layout]);
  const left = useMemo(() => layout?.get(CardGridArea.Left)?.filter((c) => !!c), [layout]);
  const right = useMemo(() => layout?.get(CardGridArea.Right)?.filter((c) => !!c), [layout]);
  const footer = useMemo(() => layout?.get(CardGridArea.Footer)?.filter((c) => !!c), [layout]);
  const panel = useMemo(() => layout?.get(CardGridArea.Panel)?.filter((c) => !!c), [layout]);

  const dataAttributes = useDataAttributes(props);

  return (
    <div
      className={clsx(props.className, style.grid)}
      ref={ref}
      id={"cards-panel-id"}
      style={props.style}
      {...dataAttributes}
      data-testid={`${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}cards-grid`}
    >
      {props.isLoading === true ? (
        <LoadingSpinner inCenter />
      ) : (
        <>
          {header != undefined && header.length > 0 && <div className={style.grid_header}>{header}</div>}
          {((left != undefined && left.length > 0) || (right != undefined && right.length > 0)) && (
            <div className={style.grid_content}>
              <div className={style.flex_content}>{left}</div>
              <div className={style.flex_content}>{right}</div>
            </div>
          )}
          {footer != undefined && footer.length > 0 && <div className={style.grid_footer}>{footer}</div>}
          {panel != undefined && panel.length > 0 && <div className={style.grid_panel}>{panel}</div>}
        </>
      )}
    </div>
  );
});

export { CardGrid, CardGridArea };
